import type { Plugin } from '../../core/collector'
import type { Collector } from '../../core/collector'

export class RoutePlugin implements Plugin {
  name = 'route'
  version = '1.0.0'

  private collector!: Collector
  private lastLocation = ''
  private appId!: number
  private autoTrack = false
  private allowHash = false
  // 初始导航期间屏蔽 handleRouteChange，防止重定向时重复上报
  private isInitializing = false

  // 延时上报
  private sendTimer: ReturnType<typeof setTimeout> | null = null
  private delay = 20 // 默认延迟 20ms，可根据需求改

  install(collector: Collector) {
    this.collector = collector
    const routerTrackingConfig = collector.config.get('routerTracking')

    // enable: false 时完全退出（App 环境由 app-page-tracking.ts + JSBridge 接管）
    if (routerTrackingConfig?.enable === false) return

    this.autoTrack = routerTrackingConfig?.autoTrack !== false
    this.allowHash = routerTrackingConfig?.allowHash ?? false
    this.appId = collector.config.get('app_id') || 0

    if (!this.autoTrack) return

    this.lastLocation = location.href
    this.hackHistoryAPI()
    this.bindNativeEvents()

    // install() 在 init() 内部同步调用，此时 started=false，需延迟到下一 tick
    // 若业务层提供了 onReady（如 router.isReady()），则等路由初始导航完成后再上报：
    // 1. 置 isInitializing=true，屏蔽初始导航期间 replaceState 触发的 handleRouteChange（防重定向重复上报）
    // 2. onReady resolve 后，更新 lastLocation 为最终 URL，再上报首次 page_view
    const onReady = routerTrackingConfig?.onReady
    if (onReady) {
      this.isInitializing = true
      onReady().then(() => {
        this.isInitializing = false
        this.lastLocation = location.href
        this.sendPageView(location.href)
      })
    } else {
      setTimeout(() => this.sendPageView(location.href), 100)
    }
  }

  private hackHistoryAPI() {
    const originalPushState = history.pushState
    history.pushState = (state, title, url) => {
      const result = originalPushState.call(history, state, title, url)
      if (this.lastLocation !== location.href) this.handleRouteChange()
      return result
    }

    const originalReplaceState = history.replaceState
    history.replaceState = (state, title, url) => {
      const result = originalReplaceState.call(history, state, title, url)
      if (this.lastLocation !== location.href) this.handleRouteChange()
      return result
    }
  }

  private bindNativeEvents() {
    window.addEventListener('popstate', () => {
      if (this.lastLocation !== location.href) this.handleRouteChange()
    })

    window.addEventListener('hashchange', () => {
      if (this.allowHash && this.lastLocation !== location.href) this.handleRouteChange()
    })
  }

  private handleRouteChange() {
    if (this.isInitializing) return

    const currentUrl = location.href

    // 延时上报，保证数据完整
    if (this.sendTimer) clearTimeout(this.sendTimer)
    this.sendTimer = setTimeout(() => {
      this.sendPageView(currentUrl)
      this.lastLocation = currentUrl
    }, this.delay)
  }

  private sendPageView(url: string) {
    const routerTrackingConfig = this.collector.config.get('routerTracking')
    if (routerTrackingConfig?.autoPageView === false) return

    const eventData = {
      url,
      page_title: document.title,
      page_path: location.pathname,
      page_host: location.host,
    }

    const eventName = routerTrackingConfig?.pageViewEvent || 'page_view'
    this.collector.track(eventName, eventData)
  }
}
